import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { enduserInterface } from 'src/app/util/interfaces/enduser';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { DepartmentInterface } from 'src/app/util/interfaces/department';
import { logInterface, logUserInterface } from 'src/app/util/interfaces/log.interface';
import { permissionI, permissionsInterface } from 'src/app/util/interfaces/customer';

@Injectable({
	providedIn: 'root',
})
export class UsersService {
	private getUserUrl = environment.baseApiUrl + 'register/allUsers?';
	private getUserDataUrl = environment.baseApiUrl + 'register/getUser/{idEnduser}';
	private getUserLogUrl = environment.baseApiUrl + 'register/logsEnduser/{idEnduser}';

	private getUserBaseFormUrl = environment.baseApiUrl + 'register/form/user/base';
	private getUserEnduserFormUrl = environment.baseApiUrl + 'register/form/user/enduser';
	private getUserAnalystFormUrl = environment.baseApiUrl + 'register/form/user/analyst';

	private getDepartmentUrl = environment.baseApiUrl + 'department/searchDepartmentByIdCust?customer={idCustomer}';
	private getLocationUrl = environment.baseApiUrl + 'location/search?customer={idCustomer}';

	private postUserUrl = environment.baseApiUrl + 'register/postUser';
	private putUserUrl = environment.baseApiUrl + 'register/putUser/{idEnduser}';

	private getPermissionDataUrl = environment.baseApiUrl + 'register/permission/{idEnduser}';
	private putPermissionDataUrl = environment.baseApiUrl + 'register/permission/{idEnduser}';

	private headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private ProfileService: ProfileService,
		private AuthLoginService: AuthLoginService,
		private http: HttpClient
	) {}

	getUserSearchForm(): Observable<genericFormInterface[]> {
		const form: genericFormInterface[] = [
			{
				name: 'email',
				label: 'lbl_geral_email',
				inputType: 'string',
				values: [''],
			},
			{
				name: 'name',
				label: 'lbl_geral_nome',
				inputType: 'string',
				values: [''],
			},
		];

		return of(form);
	}

	getUser(searchForm: genericFormInterface[]): Observable<enduserInterface[]> {
		let getUserUrl = this.getUserUrl;
		for (const formItem of searchForm) {
			if (formItem.values[0]) getUserUrl += `${formItem.name}=${formItem.values[0]}`;
		}

		return this.http.get(getUserUrl, { headers: this.headers }).pipe(
			map((endusersRes: any) => {
				const newEnduserArray: enduserInterface[] = [];
				for (const enduser of endusersRes) {
					const newEnduser: enduserInterface = {
						id: enduser.id,
						fullName: enduser.full_name,
						email: enduser.email,
					};

					newEnduserArray.push(newEnduser);
				}

				return newEnduserArray;
			})
		);
	}

	getUserLogs(id: string): Observable<any[]> {
		const getUserLogsUrl = this.getUserLogUrl.replace('{idEnduser}', '2880753');
		console.log('Fetching user logs from URL:', getUserLogsUrl);
		return this.http.get<logUserInterface[]>(getUserLogsUrl, { headers: this.headers }).pipe(
			map((logsRes: any) => {
				const newLogsArray: logUserInterface[] = [];
				const regex = [String.raw` \n `];
				logsRes.forEach((logsRes) => {
					const newLog: logUserInterface = {
						updatedBy: logsRes.quem_acessou,
						log_date: logsRes.data,
						notes: logsRes.tipo.replaceAll(regex, '<br>'),
					};
					newLogsArray.push(newLog);
				});
				return newLogsArray;
			})
		);
	}

	getUserData(id: string): Observable<any> {
		const getUserDataUrl = this.getUserDataUrl.replace('{idEnduser}', id);
		return this.http.get(getUserDataUrl, { headers: this.headers });
	}

	getUserPermissions(id: string): Observable<permissionI> {
		const getPermissionDataUrl = this.getPermissionDataUrl.replace('{idEnduser}', id);
		return this.http.get<permissionI>(getPermissionDataUrl, { headers: this.headers });
	}

	getUserBaseForm(): Observable<genericFormInterface[]> {
		return this.http.get<genericFormInterface[]>(this.getUserBaseFormUrl, { headers: this.headers });
	}

	getUserEnduserForm(): Observable<genericFormInterface[]> {
		return this.http.get<genericFormInterface[]>(this.getUserEnduserFormUrl, { headers: this.headers });
	}

	getUserAnalystForm(): Observable<genericFormInterface[]> {
		return this.http.get<genericFormInterface[]>(this.getUserAnalystFormUrl, { headers: this.headers });
	}

	postUser(bodyForm: genericFormInterface[], enduser_active: boolean, analyst_active: boolean): Observable<any> {
		const body = this.mountCRUDBody(bodyForm, analyst_active, enduser_active);

		return this.http.post(this.postUserUrl, body, { headers: this.headers });
	}

	putUser(idEnduser: string, bodyForm: genericFormInterface[], enduser_active: boolean, analyst_active: boolean): Observable<any> {
		const body = this.mountCRUDBody(bodyForm, enduser_active, analyst_active);
		const putUserUrl = this.putUserUrl.replace('{idEnduser}', idEnduser);
		return this.http.put(putUserUrl, body, { headers: this.headers });
	}

	mountCRUDBody(bodyForm: genericFormInterface[], enduser_active: boolean, analyst_active: boolean): {} {
		const body = {};
		for (const formItem of bodyForm) {
			if (formItem.inputType === 'select') {
				body[formItem.name] = formItem.values[0] ? formItem.values[0] : '';
				continue;
			}

			if (formItem.inputType === 'multi-select') {
				body[formItem.name] = [];
				for (const value of formItem.values) {
					body[formItem.name].push(value.values[0]);
				}
				continue;
			}

			if (formItem.name === 'customer_department_location') {
				body['enduser_customers'] = [];
				body['enduser_departament'] = [];
				body['enduser_location'] = [];
				body['defaultCustomer'] = [];

				for (const value of formItem.values) {
					body['enduser_customers'].push(value[0]);
					body['enduser_departament'].push(value[1]);
					body['enduser_location'].push(value[2]);
					body['defaultCustomer'].push(value[3]);
				}
				continue;
			}

			body[formItem.name] = formItem.values ? (body[formItem.name] = formItem.values[0]) : (body[formItem.name] = '');
		}

		body['analyst_active'] = analyst_active;
		body['enduser_active'] = enduser_active;

		return body;
	}

	getCustomerDepartment(idCustomer: string): Observable<genericFormInterface[]> {
		const getDepartmentUrl = this.getDepartmentUrl.replace('{idCustomer}', idCustomer);

		return this.http.get(getDepartmentUrl, { headers: this.headers }).pipe(
			map((departmentRes: any) => {
				const newDepartmentGFI: genericFormInterface[] = [];
				for (const department of departmentRes) {
					const newDeparment: genericFormInterface = {
						name: department.department,
						label: department.department,
						values: [department.department],
						rawValues: [department.id],
					};

					newDepartmentGFI.push(newDeparment);
				}

				return newDepartmentGFI;
			})
		);
	}

	getCustomerLocations(idCustomer: string): Observable<genericFormInterface[]> {
		const getLocationUrl = this.getLocationUrl.replace('{idCustomer}', idCustomer);
		return this.http.get(getLocationUrl, { headers: this.headers }).pipe(
			map((locationRes: any) => {
				const newLocationGFI: genericFormInterface[] = [];
				for (const location of locationRes) {
					const newLocation: genericFormInterface = {
						name: location.location,
						label: location.location,
						values: [location.location],
						rawValues: [location.id],
					};

					newLocationGFI.push(newLocation);
				}

				return newLocationGFI;
			})
		);
	}

	putUserPermissions(idEnduser, permissions: permissionI): Observable<any> {
		const putPermissionDataUrl = this.putPermissionDataUrl.replace('{idEnduser}', idEnduser);
		return this.http.put(putPermissionDataUrl, permissions, { headers: this.headers });
	}
}
